import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Home, Building2, Plus, Pencil, Trash2, X } from "lucide-react";

const empty = { name: "", type: "villa", order: 0, active: true };

export default function FieldOpsUnits({ embedded }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState(null);
  const { data: units = [] } = useQuery({
    queryKey: ["fo-units"],
    queryFn: async () => (await api.get("/fieldops/units?include_inactive=true")).data,
  });
  const save = useMutation({
    mutationFn: async (u) => u.id ? (await api.put(`/fieldops/units/${u.id}`, u)).data : (await api.post("/fieldops/units", u)).data,
    onSuccess: () => { toast.success("Αποθηκεύτηκε"); qc.invalidateQueries({ queryKey: ["fo-units"] }); setEdit(null); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/fieldops/units/${id}`)).data,
    onSuccess: () => { toast.success("Απενεργοποιήθηκε"); qc.invalidateQueries({ queryKey: ["fo-units"] }); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const villas = units.filter((u) => u.type === "villa");
  const common = units.filter((u) => u.type !== "villa");

  const Group = ({ title, icon: Icon, list }) => (
    <div>
      <h2 className="font-serif text-xl text-[#45423C] mb-3 inline-flex items-center gap-2"><Icon className="h-5 w-5" /> {title}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {list.map((u) => (
          <div key={u.id} data-testid={`unit-${u.id}`} className={`rounded-xl bg-white border p-4 flex items-center justify-between ${u.active ? "border-[#E3DED4]" : "border-[#E3DED4] opacity-50"}`}>
            <div><div className="font-medium text-[#45423C]">{u.name}</div>{!u.active && <span className="text-[11px] text-[#A1493A]">ανενεργό</span>}</div>
            <div className="flex gap-1">
              <button data-testid={`unit-edit-${u.id}`} onClick={() => setEdit(u)} className="h-8 w-8 rounded-lg border border-[#E3DED4] flex items-center justify-center text-[#6F6A60]"><Pencil className="h-4 w-4" /></button>
              {u.active && <button data-testid={`unit-del-${u.id}`} onClick={() => { if (window.confirm(`Απενεργοποίηση "${u.name}";`)) del.mutate(u.id); }} className="h-8 w-8 rounded-lg border border-[#E7CEC6] flex items-center justify-center text-[#A1493A]"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-8" data-testid="fo-units-page">
      <div className="flex items-end justify-between">
        <div>
          {!embedded && <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Εργασίες Πεδίου</p>}
          <h1 className={`font-serif ${embedded ? "text-2xl" : "text-3xl md:text-4xl mt-1"} text-[#45423C]`}>Μονάδες</h1>
        </div>
        <button data-testid="add-unit-btn" onClick={() => setEdit({ ...empty })} className="rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold inline-flex items-center gap-1.5"><Plus className="h-4 w-4" /> Νέα μονάδα</button>
      </div>
      <Group title="Βίλες" icon={Home} list={villas} />
      <Group title="Κοινόχρηστοι χώροι" icon={Building2} list={common} />

      {edit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" data-testid="unit-modal">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h2 className="font-serif text-2xl text-[#45423C]">{edit.id ? "Επεξεργασία" : "Νέα μονάδα"}</h2><button onClick={() => setEdit(null)}><X className="h-5 w-5 text-[#6F6A60]" /></button></div>
            <div className="space-y-3 text-sm">
              <input data-testid="unit-name" placeholder="Όνομα" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2.5" />
              <select data-testid="unit-type" value={edit.type} onChange={(e) => setEdit({ ...edit, type: e.target.value })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2.5 bg-white">
                <option value="villa">Βίλα</option><option value="common">Κοινόχρηστος χώρος</option>
              </select>
              <input data-testid="unit-order" type="number" placeholder="Σειρά" value={edit.order} onChange={(e) => setEdit({ ...edit, order: parseInt(e.target.value) || 0 })} className="w-full rounded-xl border border-[#E3DED4] px-3 py-2.5" />
              {edit.id && (
                <label className="flex items-center gap-2 text-sm text-[#45423C] cursor-pointer"><input type="checkbox" data-testid="unit-active" checked={!!edit.active} onChange={(e) => setEdit({ ...edit, active: e.target.checked })} /> Ενεργό</label>
              )}
            </div>
            <div className="mt-6 flex gap-3">
              <button data-testid="unit-save" disabled={!edit.name} onClick={() => save.mutate(edit)} className="flex-1 rounded-xl bg-[#45423C] text-white py-2.5 text-sm font-semibold disabled:opacity-50">Αποθήκευση</button>
              <button onClick={() => setEdit(null)} className="rounded-xl border border-[#E3DED4] px-4 py-2.5 text-sm">Άκυρο</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
